import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import SelectedUserMenu from "../../Assets/images/menu-icons/manage_accounts_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { GetApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { PositionDto, SportDto, UserFormData } from "../../Dto/Generic";

const CustomTableRow = ({
  label,
  value,
}: {
  label: string;
  value?: string;
}) => (
  <TableRow>
    <TableCell>{label}</TableCell>
    <TableCell>{value || "-"}</TableCell>
  </TableRow>
);

const UserView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [user, setUser] = useState<UserFormData | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [sports, setSports] = useState<SportDto[]>([]);
  const [positions, setPositions] = useState<PositionDto[]>([]);

  const getSports = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(
        "sports?page=1&limit=100&sort=createdAt&sortDirection=ASC"
      );
      setSports(res.data.data.rows);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setSports([]);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch]);

  const formatFieldName = (key: any) => {
    // Insert a space before each capital letter and capitalize the first character
    return key
      ?.replace(/([a-z])([A-Z])/g, "$1 $2")
      ?.replace(/^./, (str: any) => str.toUpperCase());
  };

  const getUser = useCallback(async () => {
    if (!id) return;
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(`user/${id}`);
      if (res.data.data.sports) {
        const positionsRes = await GetApiMethod(
          `sports/all/${res.data.data.sports}`
        );
        setPositions(positionsRes.data.data.others);
      }
      setUser(res.data.data);

      dispatch(showLoader({ showLoader: false }));
    } catch (err) {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    getUser();
    getSports();
  }, [getUser]);

  if (!user) return null;

  return (
    <AuthorizedLayout>
      <BoxHeader dashboard_name="User Management" image={SelectedUserMenu}>
        <AntTabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
        >
          <AntTab label="User Details" className="tab-menu" />
        </AntTabs>
      </BoxHeader>
      <Button
        onClick={() => navigate("/user")}
        sx={{
          my: 2,
          color: "white",
          backgroundColor: "#09BBE9",
          "&:hover": {
            backgroundColor: "#09BBE9",
          },
        }}
        startIcon={<ArrowBack />}
      >
        Back
      </Button>

      {activeTab === 0 && (
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <CustomTableRow label="First Name" value={user.firstName} />
              <CustomTableRow label="Last Name" value={user.lastName} />
              <CustomTableRow label="Email" value={user.email} />
              <CustomTableRow label="Phone" value={user.phone} />
              <CustomTableRow label="Role" value={user.roles[0]?.displayName} />
              <CustomTableRow
                label="Status"
                value={user.status ? "Active" : "Inactive"}
              />
              <CustomTableRow
                label="Date of Birth"
                value={
                  user.dateOfBirth &&
                  new Date(user.dateOfBirth).toLocaleDateString("en-AU", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                }
              />
              <CustomTableRow label="Height" value={user.height} />
              <CustomTableRow
                label="Weight"
                value={(user as any).userDetail?.weight}
              />
              <CustomTableRow
                label="Sports"
                value={sports.find((each) => each.id === user.sports)?.name}
              />
              <CustomTableRow
                label="Position"
                value={
                  positions.find((each) => each.id === user.position)?.name
                }
              />
              <CustomTableRow
                label="Preferred Position"
                value={(user as any).userDetail?.preferredPosition}
              />
              <CustomTableRow
                label="Dominant Foot"
                value={formatFieldName((user as any).userDetail?.dominantFoot)}
              />
              <CustomTableRow
                label="Skills"
                value={(user as any).userDetail?.skills}
              />
              <CustomTableRow label="Club" value={user.club} />
              <CustomTableRow label="City" value={user.city} />
              <CustomTableRow label="State" value={user.state} />
              <CustomTableRow label="Country" value={user.country} />
              <CustomTableRow label="Nationality" value={user.nationality} />
              <CustomTableRow label="Description" value={user.description} />
              <CustomTableRow
                label="Followers Count"
                value={`${(user as any).followersCount}`}
              />
              <CustomTableRow
                label="Following Count"
                value={`${(user as any).followingCount}`}
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AuthorizedLayout>
  );
};

export default UserView;
